import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import dashboardShot from '../assets/landing/dashboard.png';
import appDuo from '../assets/landing/app-duo.jpg';
import appCoach from '../assets/landing/app-coach.jpg';
import brandingMockups from '../assets/landing/branding-mockups.jpg';
import brandingPhone from '../assets/landing/branding-phone.jpg';
import wordmark from '../assets/brand/wordmark-light.png';
import './Landing.css';

const faqs = [
  {
    q: 'How do I get my studio set up?',
    a: 'You add your locations, class, schedules, and members directly in the dashboard.',
  },
  {
    q: 'How does check-in actually work?',
    a: "When class time comes, members check themselves in from their phone. The coach sees the roster live — who's checked in and who hasn't — and can check a student in from the mobile app too. No-shows are marked the same way.",
  },
  {
    q: 'Do you handle payments?',
    a: 'Members pay by cash or bank transfer, and you record it in the dashboard — tracked as memberships or drop-ins per member. Automatic membership charging is coming soon.',
  },
  {
    q: 'Can I run more than one location?',
    a: 'Yes. Add as many locations as you need, each with its own schedule, instructors, and class types — and see them together on the owner dashboard, including revenue and attendance.',
  },
  {
    q: 'Can I request a new feature?',
    a: "Yes, absolutely. Send it our way — we'll take a look at every request and consider building it, prioritizing the ones that make the platform better for everyone.",
  },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const toggleFaq = (i: number) => setOpenFaq(openFaq === i ? null : i);

  return (
    <div className="lp">
      {/* NAV */}
      <nav className="top">
        <div className="inner">
          <Link className="logo" to="/"><img src={wordmark} alt="Porrada" /></Link>
          <div className="nav-items">
            <a href="#features">Features</a>
            <a href="#roles">Roles</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
          </div>
          <div className="nav-cta">
            <Link className="btn btn-ghost" to="/login">Sign in</Link>
            <Link className="btn btn-primary" to="/book-demo">Book a Demo</Link>
            <button
              className="burger"
              aria-label="Menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="mobile-menu">
            <a href="#features" onClick={() => setMenuOpen(false)}>Features</a>
            <a href="#roles" onClick={() => setMenuOpen(false)}>Roles</a>
            <a href="#pricing" onClick={() => setMenuOpen(false)}>Pricing</a>
            <a href="#faq" onClick={() => setMenuOpen(false)}>FAQ</a>
            <Link to="/login" onClick={() => setMenuOpen(false)}>Sign in</Link>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="hero">
        <div className="container">
          <div className="badge-row">
            <span className="badge"><span className="badge-dot" />Now with multi-location support</span>
          </div>
          <h1>
            Mobile-first<br />
            gym management<br />
            <em>for MMA &amp; BJJ.</em>
          </h1>
          <p className="lede">
            Bookings, check-ins, memberships and belt tracking — run your whole
            academy from your phone.
          </p>
          <div className="hero-cta">
            <Link className="btn btn-primary btn-lg" to="/book-demo">Book a Demo</Link>
            <Link className="btn btn-ghost btn-lg" to="/login">Sign in</Link>
          </div>
          <div className="hero-trust">
            <span>Custom app branding</span>
            <span>Attendance &amp; revenue insights</span>
            <span>Multi-location ready</span>
          </div>

          {/* Showcase */}
          <div className="showcase">
            <div className="showcase-bar">
              <div className="dots"><span className="dot" /><span className="dot" /><span className="dot" /></div>
            </div>
            <img
              className="showcase-img"
              src={dashboardShot}
              alt="Porrada owner dashboard showing members, revenue, class popularity and attendance trends"
            />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="section" id="features">
        <div className="container">
          <span className="label section-eyebrow">— Features</span>
          <h2 className="section-title">Built around the moments that actually matter.</h2>
          <p className="section-sub">Three tools, sharp at one job each. No bloat, no dashboards your instructors won't open.</p>
          <div className="features">
            <div className="feat">
              <div className="feat-art">
                <svg width="100%" height="80" viewBox="0 0 280 80">
                  <line x1="0" y1="40" x2="280" y2="40" stroke="rgba(255,255,255,0.1)" />
                  <g fontFamily="JetBrains Mono" fontSize="9" fill="#5B6069">
                    <text x="0" y="36">06</text><text x="40" y="36">09</text>
                    <text x="80" y="36">12</text><text x="120" y="36">15</text>
                    <text x="160" y="36">17</text><text x="200" y="36">19</text>
                  </g>
                  <rect x="0" y="44" width="22" height="14" fill="#F2F3F5" />
                  <rect x="40" y="44" width="36" height="14" fill="#F2F3F5" />
                  <rect x="160" y="44" width="22" height="14" fill="#5B6069" />
                  <rect x="200" y="44" width="40" height="14" fill="#F2F3F5" />
                </svg>
              </div>
              <span className="feat-num">01</span>
              <h3>Check-in in seconds.</h3>
              <p>At class time, members check themselves in from their phone. The coach sees the roster live — who's in, who's not yet — and can tap a student in too. Class starts.</p>
            </div>
            <div className="feat">
              <div className="feat-art">
                <svg width="100%" height="80" viewBox="0 0 280 80">
                  <g>
                    {[0,26,52,78,104,130,156].map(x => <rect key={x} x={x} y="20" width="22" height="3" fill="#F2F3F5" />)}
                    <rect x="182" y="20" width="22" height="3" fill="rgba(255,255,255,0.18)" />
                    <rect x="208" y="20" width="22" height="3" fill="rgba(255,255,255,0.08)" />
                    <rect x="234" y="20" width="22" height="3" fill="rgba(255,255,255,0.08)" />
                  </g>
                  <text x="0" y="50" fontFamily="JetBrains Mono" fontSize="11" fill="#F2F3F5">7 / 10 booked</text>
                  <text x="0" y="68" fontFamily="JetBrains Mono" fontSize="9" fill="#5B6069">3 OPEN</text>
                </svg>
              </div>
              <span className="feat-num">02</span>
              <h3>Capacity at a glance.</h3>
              <p>Every class shows seats — booked and open — without you opening a thing. Members self-serve. You don't get DMs.</p>
            </div>
            <div className="feat">
              <div className="feat-art">
                <svg width="100%" height="80" viewBox="0 0 280 80">
                  <g fontFamily="Inter Tight" fontSize="11" fill="#F2F3F5">
                    <rect x="0" y="14" width="280" height="20" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" />
                    <text x="10" y="28">Alex Morgan</text>
                    <text x="220" y="28" fill="#F2F3F5" fontFamily="JetBrains Mono" fontSize="9">CHECKED IN</text>
                    <rect x="0" y="40" width="280" height="20" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" />
                    <text x="10" y="54">Jordan Lee</text>
                    <text x="220" y="54" fill="#F2F3F5" fontFamily="JetBrains Mono" fontSize="9">CHECKED IN</text>
                    <rect x="0" y="66" width="280" height="20" fill="transparent" stroke="rgba(255,255,255,0.06)" strokeDasharray="2 2" />
                    <text x="10" y="80">Robin Osei</text>
                    <text x="220" y="80" fill="#5B6069" fontFamily="JetBrains Mono" fontSize="9">BOOKED</text>
                  </g>
                </svg>
              </div>
              <span className="feat-num">03</span>
              <h3>Member context built in.</h3>
              <p>Attendance history, no-show count, membership status — visible on the roster, before they walk through the door.</p>
            </div>
          </div>
        </div>
      </section>

      {/* MOBILE APP */}
      <section className="section">
        <div className="container">
          <span className="label section-eyebrow">— The mobile app</span>
          <h2 className="section-title">Run the whole gym from a phone.</h2>
          <p className="section-sub">Members book and check themselves in. Coaches run the roster with one thumb. Same app, same minute.</p>
          <div className="app-shots">
            <img src={appDuo} alt="Member app — daily class schedule and one-tap self check-in" />
            <img src={appCoach} alt="Coach app — live class roster with attendance at a glance" />
          </div>
        </div>
      </section>

      {/* ROLES */}
      <section className="section" id="roles">
        <div className="container">
          <span className="label section-eyebrow">— Built for everyone in the studio</span>
          <h2 className="section-title">Four roles. One workspace. No training needed.</h2>
          <p className="section-sub">Each role sees only what they need. Less noise, fewer wrong taps, faster days.</p>
          <div className="roles">
            <div className="role">
              <div className="role-head"><span className="role-name">Members</span><span className="role-tag">— role 01</span></div>
              <ul>
                <li>Browse and book classes in seconds</li>
                <li>One-tap check-in</li>
                <li>Attendance history, no-show count, membership status</li>
              </ul>
            </div>
            <div className="role">
              <div className="role-head"><span className="role-name">Instructors</span><span className="role-tag">— role 02</span></div>
              <ul>
                <li>Today view with live and upcoming classes</li>
                <li>Check members in, mark no-shows, add notes</li>
                <li>At-a-glance stats — classes, attendances, bookings</li>
              </ul>
            </div>
            <div className="role">
              <div className="role-head"><span className="role-name">Owners + Staff</span><span className="role-tag">— role 03 / 04</span></div>
              <ul>
                <li>Multi-location dashboard with revenue &amp; attendance (can hide from staff if preferred)</li>
                <li>Record member payments — cash, bank transfer, or card</li>
                <li>Manage instructors, schedules, class types</li>
                <li>Track memberships, drop-ins &amp; expiring plans</li>
                <li>Customize app colors</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* APP BRANDING */}
      <section className="section vibe">
        <div className="container">
          <span className="label section-eyebrow">— App branding</span>
          <h2 className="section-title">Make it your vibes.</h2>
          <p className="section-sub">
            Red-on-black not your gym's vibe? Set your accent, background, and text
            in the dashboard — every member's app updates to match.
          </p>
          <picture>
            <source media="(max-width: 880px)" srcSet={brandingPhone} />
            <img
              src={brandingMockups}
              alt="Branding settings in the dashboard beside a member app preview updated to match"
            />
          </picture>
        </div>
      </section>

      {/* PRICING */}
      <section className="section" id="pricing">
        <div className="container">
          <span className="label section-eyebrow">— Pricing</span>
          <h2 className="section-title">Priced like a membership, not a mortgage.</h2>
          <p className="section-sub">Pick by gym size — pay monthly, or take two months free on yearly. Outgrown both? Go custom.</p>
          <div className="pricing">
            <div className="price">
              <span className="price-name">Starter</span>
              <div className="price-tag">
                <span className="price-amount">$50</span>
                <span className="price-unit">/ month</span>
              </div>
              <p className="price-alt">or $500 / year — 2 months free</p>
              <p className="price-desc">For academies with up to 60 active members.</p>
              <ul>
                <li>Up to 60 active members</li>
                <li>Unlimited classes, bookings &amp; check-ins</li>
                <li>Member &amp; coach mobile app</li>
                <li>Multi-location support</li>
                <li>Custom app branding</li>
              </ul>
              <Link className="btn btn-ghost btn-lg" to="/book-demo">Book a Demo</Link>
            </div>
            <div className="price featured">
              <span className="price-name">Growth</span>
              <div className="price-tag">
                <span className="price-amount">$100</span>
                <span className="price-unit">/ month</span>
              </div>
              <p className="price-alt">or $1,000 / year — 2 months free</p>
              <p className="price-desc">For academies with up to 200 active members.</p>
              <ul>
                <li>Up to 200 active members</li>
                <li>Unlimited classes, bookings &amp; check-ins</li>
                <li>Member &amp; coach mobile app</li>
                <li>Multi-location support</li>
                <li>Custom app branding</li>
              </ul>
              <Link className="btn btn-primary btn-lg" to="/book-demo">Book a Demo</Link>
            </div>
            <div className="price">
              <span className="price-name">Enterprise</span>
              <div className="price-tag">
                <span className="price-amount">Custom</span>
              </div>
              <p className="price-alt">priced by scope — let's talk</p>
              <p className="price-desc">Gyms that need tailor-made plans.</p>
              <ul>
                <li>Unlimited accounts</li>
                <li>Responsive gym website</li>
                <li>All features included</li>
              </ul>
              <Link className="btn btn-ghost btn-lg" to="/contact">Contact Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section" id="faq">
        <div className="container">
          <span className="label section-eyebrow">— FAQ</span>
          <h2 className="section-title">Questions worth answering.</h2>
          <div className="faq">
            {faqs.map((item, i) => (
              <div key={i} className={`faq-item${openFaq === i ? ' open' : ''}`}>
                <button className="faq-q" onClick={() => toggleFaq(i)}>
                  <span className="faq-q-text">{item.q}</span>
                  <span className="faq-q-icon">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && <p className="faq-a">{item.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-band">
        <h2>Less admin.<br />More mat time.</h2>
        <p>Book a demo and see your studio running in Porrada — bookings, check-ins and payments, end to end.</p>
        <div className="hero-cta">
          <Link className="btn btn-primary btn-lg" to="/book-demo">Book a Demo</Link>
          <Link className="btn btn-ghost btn-lg" to="/login">Sign in</Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="container">
          <div className="ft-brand">
            <Link className="logo" to="/"><img src={wordmark} alt="Porrada" /></Link>
            <p>Class management software for studios that don't have time for software.</p>
          </div>
          <div className="ft-col">
            <h4>Product</h4>
            <ul><li><a href="#features">Features</a></li><li><a href="#roles">Roles</a></li><li><a href="#pricing">Pricing</a></li><li><a href="#faq">FAQ</a></li></ul>
          </div>
          <div className="ft-col">
            <h4>Company</h4>
            <ul><li><Link to="/book-demo">Book a Demo</Link></li><li><Link to="/login">Sign in</Link></li></ul>
          </div>
        </div>
        <div className="container">
          <div className="ft-bot">
            <span>© {new Date().getFullYear()} Porrada · Made for studios.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
